import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save } from 'lucide-react';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { warehouseService, type Warehouse } from '@/services/warehouseService';
import {
  networkReplenishmentService,
  type AllocationMethod,
  type ApprovalMode,
  type BulkSchedulePayload,
  type BulkScheduleResult,
  type NetworkReplenishmentConfig,
  type PriorityRankMethod,
  type ReplenishmentCadence,
  type SupplyLane,
} from '@/services/networkReplenishmentService';
import { NETWORK_REPLENISHMENT_REFRESH_EVENT } from '@/utils/networkReplenishmentRefresh';

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const MONTHLY_ORDINALS = [1, 2, 3, 4, -1] as const;

const priorityInputClass =
  'w-16 rounded-md border border-gray-300 px-2 py-1 text-sm text-right focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';
const selectClass =
  'rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';

function activeWeekdays(lane: SupplyLane): number[] {
  return lane.schedules
    .filter((s) => s.is_active)
    .map((s) => s.order_weekday)
    .sort((a, b) => a - b);
}

function nextWeekdayIso(weekday: number): string {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  const current = (d.getDay() + 6) % 7; // Mon=0
  const delta = (weekday - current + 7) % 7;
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatScheduleLabel(
  cadence: string,
  weekdays: number[],
  labelDay: (day: number) => string,
  labelCadence: (c: string) => string,
): string {
  const cadenceLabel = labelCadence(cadence || 'WEEKLY');
  const days =
    weekdays.length > 0 ? weekdays.map(labelDay).join(', ') : '—';
  return `${cadenceLabel} · ${days}`;
}

export function NetworkReplenishmentConfigPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyStoreId, setBusyStoreId] = useState<number | null>(null);
  const [busyScheduleLaneId, setBusyScheduleLaneId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [config, setConfig] = useState<NetworkReplenishmentConfig | null>(null);
  const [lanes, setLanes] = useState<SupplyLane[]>([]);
  const [assortmentCount, setAssortmentCount] = useState(0);
  const [, setLanePriorityDraft] = useState<Record<number, string>>({});
  const [storePriorityDraft, setStorePriorityDraft] = useState<Record<number, string>>({});
  const [weekdayDraft, setWeekdayDraft] = useState<Record<number, number[]>>({});
  const [cadenceDraft, setCadenceDraft] = useState<Record<number, ReplenishmentCadence>>({});
  const [anchorDraft, setAnchorDraft] = useState<Record<number, string>>({});
  const [monthlyOrdinalDraft, setMonthlyOrdinalDraft] = useState<Record<number, number>>({});
  const [monthlyWeekdayDraft, setMonthlyWeekdayDraft] = useState<Record<number, number>>({});

  const [draft, setDraft] = useState({
    default_source_warehouse: 0,
    approval_mode: 'MANUAL' as ApprovalMode,
    allocation_method: 'CRITICAL_FIRST' as AllocationMethod,
    auto_send_to_erp: false,
    allow_partial_allocation: true,
    block_when_source_stock_is_unknown: true,
    excess_coverage_cycles: 2,
  });

  const [bulkDraft, setBulkDraft] = useState({
    cadence: 'WEEKLY' as ReplenishmentCadence,
    weekdays: [0, 3] as number[],
    biweekly_anchor_date: nextWeekdayIso(0),
    monthly_weekday_ordinal: 1,
    monthly_weekday: 0,
    rank_method: 'SALES_DESC' as PriorityRankMethod,
    lookback_days: 90,
    force_all: false,
  });
  const [bulkPreview, setBulkPreview] = useState<BulkScheduleResult | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [busyResetLaneId, setBusyResetLaneId] = useState<number | null>(null);

  const weekdayLabel = useCallback(
    (day: number) => t(`networkReplenishment.weekdays.${day}`),
    [t],
  );

  const cadenceLabel = useCallback(
    (cadence: string) => {
      const key = `networkReplenishment.lanes.cadences.${cadence}`;
      const translated = t(key);
      return translated === key ? cadence : translated;
    },
    [t],
  );

  const syncDrafts = useCallback((laneList: SupplyLane[]) => {
    const laneDraft: Record<number, string> = {};
    const storeDraft: Record<number, string> = {};
    const daysDraft: Record<number, number[]> = {};
    const cadences: Record<number, ReplenishmentCadence> = {};
    const anchors: Record<number, string> = {};
    const ordinals: Record<number, number> = {};
    const monthDays: Record<number, number> = {};
    laneList.forEach((lane) => {
      const days = activeWeekdays(lane);
      laneDraft[lane.id] = String(lane.priority);
      storeDraft[lane.destination_warehouse] = String(
        lane.destination_replenishment_priority ?? 1,
      );
      daysDraft[lane.id] = days;
      cadences[lane.id] = lane.cadence || 'WEEKLY';
      anchors[lane.id] =
        lane.biweekly_anchor_date || nextWeekdayIso(days[0] ?? 0);
      ordinals[lane.id] = lane.monthly_weekday_ordinal ?? 1;
      monthDays[lane.id] = lane.monthly_weekday ?? days[0] ?? 0;
    });
    setLanePriorityDraft(laneDraft);
    setStorePriorityDraft(storeDraft);
    setWeekdayDraft(daysDraft);
    setCadenceDraft(cadences);
    setAnchorDraft(anchors);
    setMonthlyOrdinalDraft(ordinals);
    setMonthlyWeekdayDraft(monthDays);
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [headerWh, cfg, laneList, assortment] = await Promise.all([
        warehouseService.getWarehouses({ is_active: true, for_header: true }),
        networkReplenishmentService.getConfig(),
        networkReplenishmentService.getSupplyLanes({ is_active: true }),
        networkReplenishmentService.getAssortment({ is_active: true, page_size: 1 }),
      ]);
      const sortedLanes = [...laneList].sort(
        (a, b) =>
          a.priority - b.priority ||
          (a.destination_warehouse_name || a.destination_warehouse_code).localeCompare(
            b.destination_warehouse_name || b.destination_warehouse_code,
          ),
      );
      setWarehouses(headerWh);
      setConfig(cfg);
      setLanes(sortedLanes);
      setAssortmentCount(assortment.count || 0);
      syncDrafts(sortedLanes);
      if (cfg) {
        setDraft({
          default_source_warehouse: cfg.default_source_warehouse,
          approval_mode: cfg.approval_mode,
          allocation_method: cfg.allocation_method,
          auto_send_to_erp: cfg.auto_send_to_erp,
          allow_partial_allocation: cfg.allow_partial_allocation,
          block_when_source_stock_is_unknown: cfg.block_when_source_stock_is_unknown,
          excess_coverage_cycles: cfg.excess_coverage_cycles ?? 2,
        });
        const days = cfg.default_order_weekdays?.length
          ? cfg.default_order_weekdays
          : [0, 3];
        setBulkDraft({
          cadence: cfg.default_cadence || 'WEEKLY',
          weekdays: days,
          biweekly_anchor_date:
            cfg.default_biweekly_anchor_date || nextWeekdayIso(days[0] ?? 0),
          monthly_weekday_ordinal: cfg.default_monthly_weekday_ordinal ?? 1,
          monthly_weekday: cfg.default_monthly_weekday ?? days[0] ?? 0,
          rank_method: cfg.priority_rank_method || 'SALES_DESC',
          lookback_days: cfg.priority_rank_lookback_days ?? 90,
          force_all: false,
        });
      } else if (headerWh.length > 0) {
        const preferred = headerWh.find((w) => w.is_default) || headerWh[0];
        setDraft((prev) => ({ ...prev, default_source_warehouse: preferred.id }));
      }
    } catch (err) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || t('networkReplenishment.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, syncDrafts]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    const onRefresh = () => void loadConfig();
    window.addEventListener(NETWORK_REPLENISHMENT_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NETWORK_REPLENISHMENT_REFRESH_EVENT, onRefresh);
  }, [loadConfig]);

  const handleSave = async () => {
    if (!draft.default_source_warehouse) {
      setError(t('networkReplenishment.config.centralRequired'));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setMessage(null);
      const saved = await networkReplenishmentService.saveConfig(draft);
      setConfig(saved);
      setMessage(t('networkReplenishment.config.saved'));
    } catch (err) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const parsePriority = (raw: string): number | null => {
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return null;
    return n;
  };

  const replaceLane = useCallback((updated: SupplyLane) => {
    setLanes((prev) =>
      prev
        .map((lane) => (lane.id === updated.id ? updated : lane))
        .sort(
          (a, b) =>
            a.priority - b.priority ||
            (a.destination_warehouse_name || a.destination_warehouse_code).localeCompare(
              b.destination_warehouse_name || b.destination_warehouse_code,
            ),
        ),
    );
    setLanePriorityDraft((prev) => ({ ...prev, [updated.id]: String(updated.priority) }));
    setStorePriorityDraft((prev) => ({
      ...prev,
      [updated.destination_warehouse]: String(
        updated.destination_replenishment_priority ?? 1,
      ),
    }));
    setWeekdayDraft((prev) => ({ ...prev, [updated.id]: activeWeekdays(updated) }));
    setCadenceDraft((prev) => ({ ...prev, [updated.id]: updated.cadence || 'WEEKLY' }));
    setAnchorDraft((prev) => ({
      ...prev,
      [updated.id]:
        updated.biweekly_anchor_date ||
        nextWeekdayIso(activeWeekdays(updated)[0] ?? 0),
    }));
    setMonthlyOrdinalDraft((prev) => ({
      ...prev,
      [updated.id]: updated.monthly_weekday_ordinal ?? 1,
    }));
    setMonthlyWeekdayDraft((prev) => ({
      ...prev,
      [updated.id]: updated.monthly_weekday ?? activeWeekdays(updated)[0] ?? 0,
    }));
  }, []);

  const applySchedule = useCallback(
    async (
      lane: SupplyLane,
      overrides?: {
        cadence?: ReplenishmentCadence;
        weekdays?: number[];
        biweekly_anchor_date?: string;
        monthly_weekday_ordinal?: number;
        monthly_weekday?: number;
      },
    ) => {
      const cadence = overrides?.cadence ?? cadenceDraft[lane.id] ?? lane.cadence ?? 'WEEKLY';
      const weekdays = overrides?.weekdays ?? weekdayDraft[lane.id] ?? activeWeekdays(lane);
      const anchor =
        overrides?.biweekly_anchor_date ??
        anchorDraft[lane.id] ??
        lane.biweekly_anchor_date ??
        nextWeekdayIso(weekdays[0] ?? 0);
      const ordinal =
        overrides?.monthly_weekday_ordinal ??
        monthlyOrdinalDraft[lane.id] ??
        lane.monthly_weekday_ordinal ??
        1;
      const monthWd =
        overrides?.monthly_weekday ??
        monthlyWeekdayDraft[lane.id] ??
        lane.monthly_weekday ??
        weekdays[0] ??
        0;

      try {
        setBusyScheduleLaneId(lane.id);
        setError(null);
        setMessage(null);
        const body =
          cadence === 'WEEKLY'
            ? { cadence, weekdays }
            : cadence === 'BIWEEKLY'
              ? {
                  cadence,
                  weekdays: weekdays.slice(0, 1).length ? weekdays.slice(0, 1) : [0],
                  biweekly_anchor_date: anchor,
                }
              : {
                  cadence,
                  monthly_weekday_ordinal: ordinal,
                  monthly_weekday: monthWd,
                };
        const updated = await networkReplenishmentService.setLaneSchedule(lane.id, body);
        replaceLane(updated);
        setMessage(t('networkReplenishment.lanes.scheduleSaved'));
      } catch (err) {
        const apiErr = err as { message?: string; detail?: string };
        setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
        await loadConfig();
      } finally {
        setBusyScheduleLaneId(null);
      }
    },
    [
      cadenceDraft,
      weekdayDraft,
      anchorDraft,
      monthlyOrdinalDraft,
      monthlyWeekdayDraft,
      replaceLane,
      t,
      loadConfig,
    ],
  );

  const handleSaveStorePriority = useCallback(
    async (lane: SupplyLane) => {
      const storeId = lane.destination_warehouse;
      const parsed = parsePriority(storePriorityDraft[storeId] ?? '');
      if (parsed === null) {
        setError(t('networkReplenishment.priority.invalid'));
        return;
      }
      try {
        setBusyStoreId(storeId);
        setError(null);
        setMessage(null);
        const [updatedWh, updatedLane] = await Promise.all([
          warehouseService.updateWarehouse(storeId, {
            replenishment_priority: parsed,
          }),
          networkReplenishmentService.updateSupplyLane(lane.id, { priority: parsed }),
        ]);
        replaceLane({
          ...updatedLane,
          destination_replenishment_priority:
            updatedWh.replenishment_priority ?? parsed,
        });
        setMessage(t('networkReplenishment.priority.storeSaved'));
      } catch (err) {
        const apiErr = err as { message?: string };
        setError(apiErr?.message || t('networkReplenishment.saveError'));
      } finally {
        setBusyStoreId(null);
      }
    },
    [storePriorityDraft, t, replaceLane],
  );

  const buildBulkPayload = useCallback((): BulkSchedulePayload => {
    const base: BulkSchedulePayload = {
      cadence: bulkDraft.cadence,
      rank_method: bulkDraft.rank_method,
      lookback_days: bulkDraft.lookback_days,
      force_all: bulkDraft.force_all,
      persist_defaults: true,
    };
    if (bulkDraft.cadence === 'WEEKLY') {
      return { ...base, weekdays: bulkDraft.weekdays };
    }
    if (bulkDraft.cadence === 'BIWEEKLY') {
      return {
        ...base,
        weekdays: [bulkDraft.weekdays[0] ?? 0],
        biweekly_anchor_date: bulkDraft.biweekly_anchor_date,
      };
    }
    return {
      ...base,
      monthly_weekday_ordinal: bulkDraft.monthly_weekday_ordinal,
      monthly_weekday: bulkDraft.monthly_weekday,
    };
  }, [bulkDraft]);

  const handleBulkPreview = useCallback(async () => {
    if (!config) {
      setError(t('networkReplenishment.bulk.needConfig'));
      return;
    }
    try {
      setBulkBusy(true);
      setError(null);
      setMessage(null);
      const result = await networkReplenishmentService.previewBulkSchedule(buildBulkPayload());
      setBulkPreview(result);
      setMessage(
        t('networkReplenishment.bulk.previewReady', {
          apply: result.apply_count,
          skip: result.skip_count,
        }),
      );
    } catch (err) {
      const apiErr = err as { message?: string; detail?: string };
      setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBulkBusy(false);
    }
  }, [config, buildBulkPayload, t]);

  const handleBulkApply = useCallback(async () => {
    if (!config) {
      setError(t('networkReplenishment.bulk.needConfig'));
      return;
    }
    const confirmed = window.confirm(
      t('networkReplenishment.bulk.confirmApply', {
        count: bulkPreview?.apply_count ?? lanes.length,
      }),
    );
    if (!confirmed) return;
    try {
      setBulkBusy(true);
      setError(null);
      setMessage(null);
      const result = await networkReplenishmentService.applyBulkSchedule(buildBulkPayload());
      setBulkPreview(result);
      setMessage(
        t('networkReplenishment.bulk.applied', {
          updated: result.updated_count ?? result.apply_count,
          skipped: result.skipped_count ?? result.skip_count,
        }),
      );
      await loadConfig();
    } catch (err) {
      const apiErr = err as { message?: string; detail?: string };
      setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBulkBusy(false);
    }
  }, [config, buildBulkPayload, bulkPreview, lanes.length, t, loadConfig]);

  const handleResetLaneSchedule = useCallback(
    async (lane: SupplyLane) => {
      try {
        setBusyResetLaneId(lane.id);
        setError(null);
        setMessage(null);
        const updated = await networkReplenishmentService.resetLaneScheduleToDefault(lane.id);
        replaceLane(updated);
        setMessage(t('networkReplenishment.bulk.resetDone'));
      } catch (err) {
        const apiErr = err as { message?: string; detail?: string };
        setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
      } finally {
        setBusyResetLaneId(null);
      }
    },
    [replaceLane, t],
  );

  const laneColumns: Column[] = useMemo(
    () => [
      {
        header: t('networkReplenishment.lanes.destination'),
        accessor: 'destination_warehouse_name',
        sortable: true,
        cell: (_value, lane: SupplyLane) => (
          <div className="min-w-[9rem] space-y-1">
            <div className="font-medium text-gray-900">
              {lane.destination_warehouse_name || lane.destination_warehouse_code}
            </div>
            <div className="text-xs text-gray-500">{lane.destination_warehouse_code}</div>
            {lane.schedule_is_custom ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                  {t('networkReplenishment.bulk.customBadge')}
                </span>
                <button
                  type="button"
                  disabled={busyResetLaneId === lane.id}
                  className="text-[11px] font-medium text-stocker-primary hover:underline disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleResetLaneSchedule(lane);
                  }}
                >
                  {busyResetLaneId === lane.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    t('networkReplenishment.bulk.useDefault')
                  )}
                </button>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        header: t('networkReplenishment.lanes.source'),
        accessor: 'source_warehouse_code',
        sortable: true,
        cell: (_value, lane: SupplyLane) => (
          <div className="min-w-[7rem]">
            <div className="text-sm text-gray-900">
              {lane.source_warehouse_name || lane.source_warehouse_code}
            </div>
            {lane.source_warehouse_name ? (
              <div className="text-xs text-gray-500">{lane.source_warehouse_code}</div>
            ) : null}
          </div>
        ),
      },
      {
        header: t('networkReplenishment.lanes.cadence'),
        accessor: 'cadence',
        cell: (_value, lane: SupplyLane) => {
          const cadence = cadenceDraft[lane.id] ?? lane.cadence ?? 'WEEKLY';
          const selected = weekdayDraft[lane.id] ?? activeWeekdays(lane);
          const busy = busyScheduleLaneId === lane.id;
          return (
            <div
              className="min-w-0 max-w-[14rem] space-y-2"
              onClick={(e) => e.stopPropagation()}
            >
              <select
                className={selectClass}
                disabled={busy}
                value={cadence}
                onChange={(e) => {
                  const next = e.target.value as ReplenishmentCadence;
                  setCadenceDraft((prev) => ({ ...prev, [lane.id]: next }));
                  void applySchedule(lane, { cadence: next });
                }}
              >
                <option value="WEEKLY">{t('networkReplenishment.lanes.cadences.WEEKLY')}</option>
                <option value="BIWEEKLY">{t('networkReplenishment.lanes.cadences.BIWEEKLY')}</option>
                <option value="MONTHLY">{t('networkReplenishment.lanes.cadences.MONTHLY')}</option>
              </select>

              {cadence === 'WEEKLY' && (
                <div className="flex flex-wrap items-center gap-1">
                  {WEEKDAYS.map((day) => {
                    const on = selected.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={busy}
                        title={weekdayLabel(day)}
                        onClick={() => {
                          const next = on
                            ? selected.filter((d) => d !== day)
                            : [...selected, day].sort((a, b) => a - b);
                          setWeekdayDraft((prev) => ({ ...prev, [lane.id]: next }));
                          void applySchedule(lane, { cadence: 'WEEKLY', weekdays: next });
                        }}
                        className={`min-w-[2rem] rounded px-1.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                          on
                            ? 'bg-stocker-primary text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {weekdayLabel(day)}
                      </button>
                    );
                  })}
                </div>
              )}

              {cadence === 'BIWEEKLY' && (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className={selectClass}
                    disabled={busy}
                    value={selected[0] ?? 0}
                    onChange={(e) => {
                      const day = Number(e.target.value);
                      const anchor = nextWeekdayIso(day);
                      setWeekdayDraft((prev) => ({ ...prev, [lane.id]: [day] }));
                      setAnchorDraft((prev) => ({ ...prev, [lane.id]: anchor }));
                      void applySchedule(lane, {
                        cadence: 'BIWEEKLY',
                        weekdays: [day],
                        biweekly_anchor_date: anchor,
                      });
                    }}
                  >
                    {WEEKDAYS.map((day) => (
                      <option key={day} value={day}>
                        {weekdayLabel(day)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    className={selectClass}
                    disabled={busy}
                    value={anchorDraft[lane.id] || ''}
                    onChange={(e) => {
                      const anchor = e.target.value;
                      setAnchorDraft((prev) => ({ ...prev, [lane.id]: anchor }));
                      void applySchedule(lane, {
                        cadence: 'BIWEEKLY',
                        weekdays: [selected[0] ?? 0],
                        biweekly_anchor_date: anchor,
                      });
                    }}
                  />
                </div>
              )}

              {cadence === 'MONTHLY' && (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className={selectClass}
                    disabled={busy}
                    value={monthlyOrdinalDraft[lane.id] ?? 1}
                    onChange={(e) => {
                      const ordinal = Number(e.target.value);
                      setMonthlyOrdinalDraft((prev) => ({ ...prev, [lane.id]: ordinal }));
                      void applySchedule(lane, {
                        cadence: 'MONTHLY',
                        monthly_weekday_ordinal: ordinal,
                        monthly_weekday: monthlyWeekdayDraft[lane.id] ?? 0,
                      });
                    }}
                  >
                    {MONTHLY_ORDINALS.map((ord) => (
                      <option key={ord} value={ord}>
                        {t(`networkReplenishment.lanes.ordinals.${ord}`)}
                      </option>
                    ))}
                  </select>
                  <select
                    className={selectClass}
                    disabled={busy}
                    value={monthlyWeekdayDraft[lane.id] ?? 0}
                    onChange={(e) => {
                      const day = Number(e.target.value);
                      setMonthlyWeekdayDraft((prev) => ({ ...prev, [lane.id]: day }));
                      void applySchedule(lane, {
                        cadence: 'MONTHLY',
                        monthly_weekday_ordinal: monthlyOrdinalDraft[lane.id] ?? 1,
                        monthly_weekday: day,
                      });
                    }}
                  >
                    {WEEKDAYS.map((day) => (
                      <option key={day} value={day}>
                        {weekdayLabel(day)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : null}
            </div>
          );
        },
      },
      {
        header: t('networkReplenishment.lanes.nextOrder'),
        accessor: 'next_cycle',
        cell: (_value, lane: SupplyLane) => lane.next_cycle?.order_date || '—',
      },
      {
        header: t('networkReplenishment.lanes.nextReceipt'),
        accessor: 'id',
        cell: (_value, lane: SupplyLane) => lane.next_cycle?.expected_receipt_date || '—',
      },
      {
        header: t('networkReplenishment.lanes.priority'),
        accessor: 'priority',
        sortable: true,
        align: 'right',
        cell: (_value, lane: SupplyLane) => (
          <span className="text-sm text-gray-700" title={t('networkReplenishment.bulk.lanePriorityHint')}>
            {lane.priority}
          </span>
        ),
      },
      {
        header: t('networkReplenishment.lanes.storePriority'),
        accessor: 'destination_replenishment_priority',
        sortable: true,
        align: 'right',
        cell: (_value, lane: SupplyLane) => (
          <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            <input
              type="number"
              min={0}
              step={1}
              className={priorityInputClass}
              value={
                storePriorityDraft[lane.destination_warehouse] ??
                String(lane.destination_replenishment_priority ?? 1)
              }
              onChange={(e) =>
                setStorePriorityDraft((prev) => ({
                  ...prev,
                  [lane.destination_warehouse]: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSaveStorePriority(lane);
                }
              }}
            />
            <button
              type="button"
              disabled={busyStoreId === lane.destination_warehouse}
              className="text-xs font-medium text-stocker-primary hover:underline disabled:opacity-50"
              onClick={() => void handleSaveStorePriority(lane)}
            >
              {busyStoreId === lane.destination_warehouse ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                t('networkReplenishment.priority.save')
              )}
            </button>
          </div>
        ),
      },
    ],
    [
      t,
      weekdayLabel,
      cadenceDraft,
      weekdayDraft,
      anchorDraft,
      monthlyOrdinalDraft,
      monthlyWeekdayDraft,
      busyScheduleLaneId,
      storePriorityDraft,
      busyStoreId,
      busyResetLaneId,
      applySchedule,
      handleSaveStorePriority,
      handleResetLaneSchedule,
    ],
  );

  const fieldClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        {t('networkReplenishment.loading')}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-6 p-4 md:p-6">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {message}
        </div>
      )}

      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('networkReplenishment.config.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t('networkReplenishment.config.subtitle')}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('networkReplenishment.config.central')}
            </span>
            <select
              className={fieldClass}
              value={draft.default_source_warehouse || ''}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  default_source_warehouse: Number(e.target.value),
                }))
              }
            >
              <option value="">{t('networkReplenishment.config.selectWarehouse')}</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('networkReplenishment.config.approvalMode')}
            </span>
            <select
              className={fieldClass}
              value={draft.approval_mode}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  approval_mode: e.target.value as ApprovalMode,
                }))
              }
            >
              <option value="MANUAL">{t('networkReplenishment.config.modes.MANUAL')}</option>
              <option value="AUTOMATIC">{t('networkReplenishment.config.modes.AUTOMATIC')}</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('networkReplenishment.config.allocationMethod')}
            </span>
            <select
              className={fieldClass}
              value={draft.allocation_method}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  allocation_method: e.target.value as AllocationMethod,
                }))
              }
            >
              <option value="CRITICAL_FIRST">
                {t('networkReplenishment.config.methods.CRITICAL_FIRST')}
              </option>
              <option value="PRIORITY">{t('networkReplenishment.config.methods.PRIORITY')}</option>
              <option value="PROPORTIONAL">
                {t('networkReplenishment.config.methods.PROPORTIONAL')}
              </option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('networkReplenishment.config.excessCoverageCycles')}
            </span>
            <input
              type="number"
              min={1}
              className={fieldClass}
              value={draft.excess_coverage_cycles}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  excess_coverage_cycles: Math.max(1, Number(e.target.value) || 1),
                }))
              }
            />
            <span className="mt-1 block text-xs text-gray-500">
              {t('networkReplenishment.config.excessCoverageCyclesHelp')}
            </span>
          </label>

          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={draft.allow_partial_allocation}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    allow_partial_allocation: e.target.checked,
                  }))
                }
              />
              {t('networkReplenishment.config.allowPartial')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={draft.block_when_source_stock_is_unknown}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    block_when_source_stock_is_unknown: e.target.checked,
                  }))
                }
              />
              {t('networkReplenishment.config.blockUnknownStock')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={draft.auto_send_to_erp}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, auto_send_to_erp: e.target.checked }))
                }
              />
              {t('networkReplenishment.config.autoSendErp')}
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-2 rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('networkReplenishment.config.save')}
          </button>
          {config && (
            <span className="text-xs text-gray-500">
              {t('networkReplenishment.config.lastUpdated')}:{' '}
              {new Date(config.updated_at).toLocaleString()}
            </span>
          )}
        </div>
      </section>

      <section className="min-w-0 space-y-4 rounded-lg border border-gray-200 bg-white p-5">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">
            {t('networkReplenishment.bulk.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t('networkReplenishment.bulk.subtitle')}</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                {t('networkReplenishment.lanes.cadence')}
              </span>
              <select
                className={selectClass}
                value={bulkDraft.cadence}
                onChange={(e) =>
                  setBulkDraft((prev) => ({
                    ...prev,
                    cadence: e.target.value as ReplenishmentCadence,
                  }))
                }
              >
                <option value="WEEKLY">{t('networkReplenishment.lanes.cadences.WEEKLY')}</option>
                <option value="BIWEEKLY">{t('networkReplenishment.lanes.cadences.BIWEEKLY')}</option>
                <option value="MONTHLY">{t('networkReplenishment.lanes.cadences.MONTHLY')}</option>
              </select>
            </label>

            {bulkDraft.cadence === 'WEEKLY' && (
              <div className="flex flex-wrap items-center gap-1">
                {WEEKDAYS.map((day) => {
                  const on = bulkDraft.weekdays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      title={weekdayLabel(day)}
                      onClick={() =>
                        setBulkDraft((prev) => ({
                          ...prev,
                          weekdays: on
                            ? prev.weekdays.filter((d) => d !== day)
                            : [...prev.weekdays, day].sort((a, b) => a - b),
                        }))
                      }
                      className={`min-w-[2rem] rounded px-1.5 py-1 text-[11px] font-medium transition-colors ${
                        on
                          ? 'bg-stocker-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {weekdayLabel(day)}
                    </button>
                  );
                })}
              </div>
            )}

            {bulkDraft.cadence === 'BIWEEKLY' && (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className={selectClass}
                  value={bulkDraft.weekdays[0] ?? 0}
                  onChange={(e) => {
                    const day = Number(e.target.value);
                    setBulkDraft((prev) => ({
                      ...prev,
                      weekdays: [day],
                      biweekly_anchor_date: nextWeekdayIso(day),
                    }));
                  }}
                >
                  {WEEKDAYS.map((day) => (
                    <option key={day} value={day}>
                      {weekdayLabel(day)}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  className={selectClass}
                  value={bulkDraft.biweekly_anchor_date}
                  onChange={(e) =>
                    setBulkDraft((prev) => ({
                      ...prev,
                      biweekly_anchor_date: e.target.value,
                    }))
                  }
                />
              </div>
            )}

            {bulkDraft.cadence === 'MONTHLY' && (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className={selectClass}
                  value={bulkDraft.monthly_weekday_ordinal}
                  onChange={(e) =>
                    setBulkDraft((prev) => ({
                      ...prev,
                      monthly_weekday_ordinal: Number(e.target.value),
                    }))
                  }
                >
                  {MONTHLY_ORDINALS.map((ord) => (
                    <option key={ord} value={ord}>
                      {t(`networkReplenishment.lanes.ordinals.${ord}`)}
                    </option>
                  ))}
                </select>
                <select
                  className={selectClass}
                  value={bulkDraft.monthly_weekday}
                  onChange={(e) =>
                    setBulkDraft((prev) => ({
                      ...prev,
                      monthly_weekday: Number(e.target.value),
                    }))
                  }
                >
                  {WEEKDAYS.map((day) => (
                    <option key={day} value={day}>
                      {weekdayLabel(day)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                {t('networkReplenishment.bulk.rankMethod')}
              </span>
              <select
                className={fieldClass}
                value={bulkDraft.rank_method}
                onChange={(e) =>
                  setBulkDraft((prev) => ({
                    ...prev,
                    rank_method: e.target.value as PriorityRankMethod,
                  }))
                }
              >
                <option value="SALES_DESC">
                  {t('networkReplenishment.bulk.methods.SALES_DESC')}
                </option>
                <option value="ALPHA">{t('networkReplenishment.bulk.methods.ALPHA')}</option>
                <option value="KEEP">{t('networkReplenishment.bulk.methods.KEEP')}</option>
              </select>
            </label>
            {bulkDraft.rank_method === 'SALES_DESC' && (
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">
                  {t('networkReplenishment.bulk.lookbackDays')}
                </span>
                <input
                  type="number"
                  min={1}
                  className={fieldClass}
                  value={bulkDraft.lookback_days}
                  onChange={(e) =>
                    setBulkDraft((prev) => ({
                      ...prev,
                      lookback_days: Number(e.target.value) || 90,
                    }))
                  }
                />
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={bulkDraft.force_all}
                onChange={(e) =>
                  setBulkDraft((prev) => ({ ...prev, force_all: e.target.checked }))
                }
              />
              {t('networkReplenishment.bulk.forceAll')}
            </label>
            <p className="text-xs text-gray-500">{t('networkReplenishment.bulk.help')}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={bulkBusy || !config}
            onClick={() => void handleBulkPreview()}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('networkReplenishment.bulk.preview')}
          </button>
          <button
            type="button"
            disabled={bulkBusy || !config}
            onClick={() => void handleBulkApply()}
            className="inline-flex items-center gap-2 rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('networkReplenishment.bulk.apply', {
              count: bulkPreview?.apply_count ?? lanes.length,
            })}
          </button>
        </div>

        {bulkPreview && bulkPreview.lanes.length > 0 && (
          <div className="min-w-0 max-w-full overflow-x-auto rounded-md border border-gray-100">
            <table className="w-full min-w-[36rem] text-left text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('networkReplenishment.lanes.destination')}</th>
                  <th className="px-3 py-2 font-medium">{t('networkReplenishment.bulk.colCurrent')}</th>
                  <th className="px-3 py-2 font-medium">{t('networkReplenishment.bulk.colProposed')}</th>
                  <th className="px-3 py-2 font-medium">{t('networkReplenishment.lanes.storePriority')}</th>
                  <th className="px-3 py-2 font-medium">{t('networkReplenishment.bulk.colMetric')}</th>
                  <th className="px-3 py-2 font-medium">{t('networkReplenishment.bulk.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {bulkPreview.lanes.map((row) => (
                  <tr key={row.lane_id} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{row.destination_name}</div>
                      <div className="text-gray-500">{row.destination_code}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {formatScheduleLabel(
                        row.current_cadence,
                        row.current_weekdays,
                        weekdayLabel,
                        cadenceLabel,
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {formatScheduleLabel(
                        row.proposed_cadence,
                        row.proposed_weekdays,
                        weekdayLabel,
                        cadenceLabel,
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {t('networkReplenishment.bulk.priorityChange', {
                        from: row.current_store_priority,
                        to: row.proposed_store_priority ?? '—',
                      })}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {row.rank_metric != null
                        ? t('networkReplenishment.bulk.metricSales', {
                            value: row.rank_metric.toLocaleString(),
                          })
                        : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {row.will_skip_custom ? (
                        <span className="text-amber-700">
                          {t('networkReplenishment.bulk.skipCustom')}
                        </span>
                      ) : (
                        <span className="text-emerald-700">
                          {t('networkReplenishment.bulk.willApply')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">
              {t('networkReplenishment.lanes.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{t('networkReplenishment.lanes.subtitle')}</p>
            <p className="mt-1 text-xs text-gray-500">{t('networkReplenishment.lanes.scheduleHelp')}</p>
            <p className="mt-0.5 text-xs text-gray-500">{t('networkReplenishment.priority.help')}</p>
          </div>
          <span className="text-sm text-gray-600">
            {t('networkReplenishment.assortment.count', { count: assortmentCount })}
          </span>
        </div>
        <div className="min-w-0 max-w-full">
          <DataTable
            columns={laneColumns}
            data={lanes}
            pagination
            compact
          />
        </div>
        {lanes.length === 0 && (
          <p className="text-center text-sm text-gray-500">{t('networkReplenishment.lanes.empty')}</p>
        )}
      </section>
    </div>
  );
}
